import React, { HTMLAttributes } from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'

const paddingSize = 16
const containerCss = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const columnsCss = css`
  display: flex;
  height: 100%;
  width: 100%;
`
const leftColumnCss = css`
  height: 100%;
  width: 20%;
  min-width: 200px;
  overflow: auto;
  border-right: 1px ${theme.color.neutral40} solid;
`

const headerCss = css`
  display: flex;
  align-items: center;
  width: 100%;
  margin: ${paddingSize / 2}px 0;
`

const middleColumnCss = css`
  flex: 1;
  padding: 0 ${paddingSize}px 0 ${paddingSize}px;
  overflow: auto;

  /* below overrides global style in contrast-ui to give us rounded table border */
  table {
    border-collapse: unset;
  }
`
interface PageLayoutProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  header: React.ReactNode
  content: React.ReactNode
  contentLeft?: React.ReactNode
}

export const PageLayout = ({ header, content, contentLeft = null, ...props }: PageLayoutProps) => {
  // this height code assumes the PageLayout takes the entire bottom half of a page
  // typically this is the case since the nav bar is always present
  // * adjustment for a footer section would have to be handled when necessary
  const layoutRef = React.useRef<HTMLDivElement>(null)
  const [height, setHeight] = React.useState<string>('100%')
  React.useLayoutEffect(() => {
    if (layoutRef.current) {
      setHeight(`calc(100vh - ${layoutRef.current.offsetTop}px)`)
    }
  }, [setHeight])

  return (
    <div
      {...props}
      className={containerCss}
      ref={layoutRef}
      style={{ height }}
      data-testid="page-layout-container"
    >
      <div className={columnsCss} data-testid="page-layout-columns">
        {contentLeft && (
          <section className={leftColumnCss} data-testid="page-layout-content-left">
            {contentLeft}
          </section>
        )}
        <section className={middleColumnCss} data-testid="page-layout-middle">
          <header className={headerCss} data-testid="page-layout-middle-header">
            {header}
          </header>
          <section data-testid="page-layout-middle-content">{content}</section>
        </section>
      </div>
    </div>
  )
}
